import { Injectable, effect, signal, computed } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Subject } from 'rxjs';

export interface LayoutConfig {
  preset: string;
  primary: string;
  surface: string | undefined | null;
  darkTheme: boolean;
  menuMode: string;
}

interface LayoutState {
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  configSidebarVisible: boolean;
  mobileMenuActive: boolean;
  menuHoverActive: boolean;
  activePath: string | null;
}

// Viewport threshold (px) at/below which the desktop sidebar auto-collapses:
// (a) starts collapsed by default after login;
// (b) re-collapses after selecting a leaf menu item (frees horizontal table space).
// Targets HD 1366×768 (e.g., Lenovo T490 entry-level). Above this width the sidebar
// behaves like before (FHD 1920×1080 stays expanded; manual toggle still available).
const SIDEBAR_AUTO_COLLAPSE_BREAKPOINT_PX = 1366;

function isAutoCollapseViewport(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.innerWidth <= SIDEBAR_AUTO_COLLAPSE_BREAKPOINT_PX
  );
}

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  layoutConfig = signal<LayoutConfig>({
    preset: 'Aura',
    primary: 'emerald',
    surface: null,
    darkTheme: false,
    menuMode: 'static',
  });

  layoutState = signal<LayoutState>({
    staticMenuDesktopInactive: isAutoCollapseViewport(),
    overlayMenuActive: false,
    configSidebarVisible: false,
    mobileMenuActive: false,
    menuHoverActive: false,
    activePath: null,
  });

  // Menu items consolidated here (decision C3.4) — replaces the separate MenuService from the v17 port.
  menuItems = signal<MenuItem[]>([]);

  // Title shown in the topbar — each app sets it in app.component.ts ngOnInit.
  // Generic default; the v17 port used the hardcoded title "DGACONTROL - RESOLUCION 1238".
  appTitle = signal<string>('DGA Control');

  // Footer text — empty by default (no footer rendered). Each app can set it if needed.
  // The v17 port did not render a footer; Sakai v21 shipped "SAKAI by PrimeNG" hardcoded (removed in 2.5.9).
  footerText = signal<string>('');

  // Replaces AppTopbarService.logout$ from the v17 port. The topbar emits when the user
  // clicks logout; each app subscribes in its app.component.ts to call AuthService.logout().
  logoutRequested$ = new Subject<void>();

  theme = computed(() => (this.layoutConfig().darkTheme ? 'light' : 'dark'));

  isSidebarActive = computed(
    () =>
      this.layoutState().overlayMenuActive ||
      this.layoutState().mobileMenuActive,
  );

  getPrimary = computed(() => this.layoutConfig().primary);

  getSurface = computed(() => this.layoutConfig().surface);

  isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

  transitionComplete = signal<boolean>(false);

  private initialized = false;

  constructor() {
    effect(() => {
      const config = this.layoutConfig();

      // Skip the very first emission: the initial config is applied by each app's
      // app.component, not by this effect. (`config` itself is never nullish — the
      // signal is seeded with a literal — so it is not part of the guard.)
      if (!this.initialized) {
        this.initialized = true;
        return;
      }

      this.handleDarkModeTransition(config);
    });
  }

  private handleDarkModeTransition(config: LayoutConfig): void {
    const supportsViewTransition = 'startViewTransition' in document;

    if (supportsViewTransition) {
      this.startViewTransition(config);
    } else {
      this.toggleDarkMode(config);
    }
  }

  private startViewTransition(config: LayoutConfig): void {
    document.startViewTransition(() => {
      this.toggleDarkMode(config);
    });
  }

  toggleDarkMode(config?: LayoutConfig): void {
    const _config = config ?? this.layoutConfig();
    if (_config.darkTheme) {
      document.documentElement.classList.add('app-dark');
    } else {
      document.documentElement.classList.remove('app-dark');
    }
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.layoutState.update((prev) => ({
        ...prev,
        overlayMenuActive: !this.layoutState().overlayMenuActive,
      }));
    }

    if (this.isDesktop()) {
      this.layoutState.update((prev) => ({
        ...prev,
        staticMenuDesktopInactive:
          !this.layoutState().staticMenuDesktopInactive,
      }));
    } else {
      this.layoutState.update((prev) => ({
        ...prev,
        mobileMenuActive: !this.layoutState().mobileMenuActive,
      }));
    }
  }

  showConfigSidebar() {
    this.layoutState.update((prev) => ({
      ...prev,
      configSidebarVisible: true,
    }));
  }

  hideConfigSidebar() {
    this.layoutState.update((prev) => ({
      ...prev,
      configSidebarVisible: false,
    }));
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isMobile() {
    return !this.isDesktop();
  }

  // True when the current viewport is at or below the HD auto-collapse threshold.
  // Consumers: layoutState init (start collapsed) + app.menuitem.itemClick (close
  // sidebar after leaf navigation so wide tables get the full horizontal space).
  isAutoCollapseViewport(): boolean {
    return isAutoCollapseViewport();
  }
}
